import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ILoginRequest, IUser, IUserInfo } from "../../models/Users";
import { IJwtPayload } from "../../models/common";
import { authService } from "../../services/authService";
import { tokenStorage } from "../../utils/storage";
import { jwtDecode } from "jwt-decode";

interface AuthState {
  user: IUser | null;
  userInfo: IUserInfo | null;
  isInitializing: boolean;
}
const initialState: AuthState = {
  user: null,
  userInfo: null,
  isInitializing: false,
};

export const login = createAsyncThunk<
  IUser,
  ILoginRequest,
  { rejectValue: { statusCode: number; message: string } }
>("auth/login", async (data, thunkAPI) => {
  try {
    const res = await authService.login(data);
    return res;
  } catch (err: any) {
    const statusCode =
      err.response?.data?.statusCode || err.response?.status || 500;
    const message =
      err.response?.data?.message || err.message || "Đăng nhập thất bại";
    return thunkAPI.rejectWithValue({ statusCode, message });
  }
});

export const fetchMe = createAsyncThunk<
  IUserInfo,
  void,
  { rejectValue: string }
>("auth/fetchMe", async (_, thunkAPI) => {
  try {
    const res = await authService.me();
    return res;
  } catch (err: any) {
    const message =
      err.response?.data?.message ||
      err.message ||
      "Lấy thông tin user thất bại";
    return thunkAPI.rejectWithValue(message);
  }
});

export const logout = createAsyncThunk<any, void, { rejectValue: string }>(
  "auth/logout",
  async (_, thunkAPI) => {
    try {
      const res = await authService.logout();
      return res;
    } catch (err: any) {
      const message =
        err.response?.data?.message || err.message || "Đăng xuất thất bại";
      return thunkAPI.rejectWithValue(message);
    }
  },
);
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    startInitializing: (state) => {
      state.isInitializing = true;
    },
    loadingUser: (state) => {
      const token = tokenStorage.get();
      if (token) {
        const user: IJwtPayload = jwtDecode(token);
        state.user = {
          accessToken: token,
          user: {
            id: user.id,
            email: user.email,
            role: user.role,
            fullName: user.fullName,
            department: {
              departmentCode: user?.department?.departmentCode,
            },
            position: {
              positionCode: user?.position?.positionCode,
            },
          },
        };
      }
    },
    setUser: (state, action) => {
      state.userInfo = action.payload;
    },
    finishInitializing: (state) => {
      state.isInitializing = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.fulfilled, (state, action) => {
      tokenStorage.set(action.payload.accessToken);
      state.user = action.payload;
    });
    builder.addCase(fetchMe.pending, (state) => {
      state.isInitializing = true;
    });
    builder.addCase(fetchMe.fulfilled, (state, action) => {
      state.userInfo = action.payload;
      state.isInitializing = false;
    });
    builder.addCase(fetchMe.rejected, (state) => {
      tokenStorage.clear();
      state.user = null;
      state.userInfo = null;
      state.isInitializing = false;
    });
    builder.addCase(logout.fulfilled, (state) => {
      tokenStorage.clear();
      state.user = null;
      state.userInfo = null;
    });
  },
});
export const { loadingUser, setUser, startInitializing, finishInitializing } =
  authSlice.actions;
export default authSlice.reducer;
